import { useState, useEffect, useMemo, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, KeyRound, Check, CircleAlert, PartyPopper } from "lucide-react";
import { API_BASE_URL } from "@/utils/env";
import { BRAND } from "@/brand";

const ACCENT = BRAND === "corporate" ? "#E8890B" : "#1976D2";
const BRAND_NAME = BRAND === "corporate" ? "Run & Comply" : "Grow & Raise";

function passwordStrength(pw: string): { label: string; score: 0 | 1 | 2 | 3; color: string } {
  if (!pw) return { label: "", score: 0, color: "transparent" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[0-9]/.test(pw) && /[a-zA-Z]/.test(pw)) score++;
  if (pw.length >= 10 && /[^a-zA-Z0-9]/.test(pw)) score++;
  if (score >= 3) return { label: "Strong", score: 3, color: "#16A34A" };
  if (score === 2) return { label: "Good", score: 2, color: "#CA8A04" };
  return { label: "Weak", score: 1, color: "#DC2626" };
}

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  // Admin reset links carry ?admin=1 so we send them back to the admin portal
  // instead of the client login page.
  const isAdminReset = searchParams.get("admin") === "1";
  const loginPath = isAdminReset ? "/admin-login" : "/login";
  const forgotPath = isAdminReset ? "/admin/forgot-password" : "/forgot-password";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(3);

  const strength = useMemo(() => passwordStrength(newPassword), [newPassword]);
  const matches = confirmPassword.length > 0 && newPassword === confirmPassword;
  const mismatches = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    if (!newPassword.trim() || !confirmPassword.trim()) {
      setError("Please enter both password fields");
      return;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!token) {
      setError("Invalid reset token");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to reset password");
      }
      setSuccess(true);
    } catch (err: any) {
      console.error("Reset password error:", err);
      setError(err.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Countdown + redirect once the reset succeeds.
  useEffect(() => {
    if (!success) return;
    if (secondsLeft <= 0) { navigate(loginPath); return; }
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [success, secondsLeft, navigate, loginPath]);

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_1fr]" style={{ fontFamily: "'Montserrat',system-ui,sans-serif" }}>
      {/* Left — form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12 bg-white">
        <div className="max-w-md w-full mx-auto">
          <Link to={loginPath} className="flex items-center gap-2 mb-8">
            <span className="w-9 h-9 rounded-xl grid place-items-center text-white" style={{ background: ACCENT }}>
              <ShieldCheck className="w-5 h-5" />
            </span>
            <div>
              <div className="text-[15px] font-black text-slate-900 leading-none">
                Conncct{isAdminReset && <> <span style={{ color: ACCENT }}>Admin</span></>}
              </div>
              <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-slate-400 mt-1">{BRAND_NAME}</div>
            </div>
          </Link>

          {!token ? (
            <>
              <div className="w-14 h-14 rounded-full grid place-items-center mb-5 bg-red-50 text-red-600">
                <CircleAlert className="w-7 h-7" />
              </div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">This link is invalid</h1>
              <p className="text-sm text-slate-500 mt-1.5 mb-7">
                The reset link is missing its token — it may have been copied incorrectly. Request a fresh one below.
              </p>
              <Link
                to={forgotPath}
                className="inline-block w-full text-center rounded-xl py-3 text-sm font-bold text-white"
                style={{ background: ACCENT }}
              >
                Request a new link
              </Link>
              <Link to={loginPath} className="inline-block mt-6 text-sm font-semibold text-slate-500 hover:text-slate-800">
                ← Back to login
              </Link>
            </>
          ) : !success ? (
            <>
              <div className="w-12 h-12 rounded-2xl grid place-items-center mb-5" style={{ background: `${ACCENT}1a`, color: ACCENT }}>
                <KeyRound className="w-6 h-6" />
              </div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Create a new password</h1>
              <p className="text-sm text-slate-500 mt-1.5 mb-7">Your new password must be different from previously used passwords.</p>

              {error && (
                <div className="mb-4 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1.5">New password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      disabled={loading}
                      className="w-full rounded-xl border border-slate-300 pl-3.5 pr-11 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 h-4">
                    <div className="flex-1 h-1 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${(strength.score / 3) * 100}%`, background: strength.color }}
                      />
                    </div>
                    <span className="text-[11px] font-bold" style={{ color: newPassword ? strength.color : "#94A3B8" }}>
                      {newPassword ? strength.label : "Min. 6 characters"}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1.5">Confirm password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                      disabled={loading}
                      className={`w-full rounded-xl border pl-3.5 pr-11 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 ${
                        mismatches
                          ? "border-red-300 focus:ring-red-100 focus:border-red-400"
                          : "border-slate-300 focus:ring-slate-200 focus:border-slate-400"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {matches && (
                    <p className="flex items-center gap-1 mt-1.5 text-[11px] font-bold text-green-600">
                      <Check className="w-3 h-3" /> Passwords match
                    </p>
                  )}
                  {mismatches && <p className="mt-1.5 text-[11px] font-bold text-red-600">Passwords don't match</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl py-3 text-sm font-bold text-white disabled:opacity-60"
                  style={{ background: ACCENT }}
                >
                  {loading ? "Resetting…" : "Reset password"}
                </button>
              </form>

              <Link to={loginPath} className="inline-block mt-6 text-sm font-semibold text-slate-500 hover:text-slate-800">
                ← Back to login
              </Link>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-full grid place-items-center mb-5 bg-green-50 text-green-600">
                <PartyPopper className="w-7 h-7" />
              </div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Password reset</h1>
              <p className="text-sm text-slate-500 mt-1.5">Your password has been changed. You can now sign in with your new password.</p>
              <p className="text-xs text-slate-400 mt-3 mb-7">Redirecting to login in {secondsLeft}s…</p>
              <Link
                to={loginPath}
                className="inline-block w-full text-center rounded-xl py-3 text-sm font-bold text-white"
                style={{ background: ACCENT }}
              >
                Go to login now
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Right — brand rail */}
      <div
        className="hidden lg:flex flex-col justify-center px-16 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(150deg, #17263A, #0E1A2B)" }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute", width: 420, height: 420, borderRadius: "50%", top: -140, right: -120,
            background: `radial-gradient(circle, ${ACCENT}55, transparent 70%)`, filter: "blur(14px)",
          }}
        />
        <div className="relative z-10 max-w-md">
          <div className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: ACCENT === "#1976D2" ? "#8fc0f5" : "#F5B841" }}>
            Conncct{isAdminReset ? " Admin" : ""} · {BRAND_NAME}
          </div>
          <h2 className="text-3xl font-black tracking-tight mt-3 leading-tight">A fresh, secure password.</h2>
          <p className="text-[15px] text-slate-300 mt-4 leading-relaxed">
            Pick something you haven't used here before — this link only works once and expires soon after it's opened.
          </p>
          <ul className="mt-8 space-y-3">
            {["One-time link, used once", "Expires 60 minutes after it's sent", "Every reset is audit-logged"].map(t => (
              <li key={t} className="flex items-center gap-3 text-sm text-slate-200">
                <span className="w-5 h-5 rounded-full grid place-items-center flex-none" style={{ background: ACCENT }}>
                  <Check className="w-3 h-3 text-white" />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
